"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

/**
 * Defines how a tooltip trigger should react to non-hover activation.
 *
 * - "hover": use for actionable controls such as links, icon buttons, the
 *   theme toggle and material links. The tooltip opens on mouse/pen hover and
 *   keyboard focus-visible, must not open on touch, and must not intercept
 *   click, Enter or Space activation.
 * - "toggle": use for passive metadata controls such as Hint, LanguageFlag,
 *   JavaChampionBadge and small badges. The tooltip opens on mouse/pen hover
 *   and keyboard focus-visible, toggles on click/tap/Enter/Space, closes on
 *   Escape and outside click, and suppresses hover reopening after manual close
 *   until the pointer leaves the shared hover region. If keyboard focus already
 *   opened it, Enter or Space closes the current open state; pressing again
 *   opens it.
 */
type TooltipInteraction = "hover" | "toggle";

/**
 * Options accepted by internal close helpers.
 */
type CloseTooltipOptions = {
  /**
   * Prevents an immediately closed tooltip from reopening because the pointer
   * is still resting on the trigger. The suppression is cleared once the
   * pointer leaves the trigger/content region.
   */
  suppressHoverUntilPointerLeave?: boolean;
};

/**
 * Shared state and event handlers used by TooltipTrigger and TooltipContent.
 *
 * The context keeps the behavioral logic in Tooltip while allowing the public
 * API to stay compositional.
 */
type TooltipContextValue = {
  open: boolean;
  disabled: boolean;
  interaction: TooltipInteraction;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentRef: React.MutableRefObject<HTMLElement | null>;
  openTooltip: () => void;
  closeTooltip: (options?: CloseTooltipOptions) => void;
  isInsideTooltip: (target: EventTarget | null) => boolean;
  handleTriggerPointerEnter: React.PointerEventHandler<HTMLElement>;
  handleTriggerPointerLeave: React.PointerEventHandler<HTMLElement>;
  handleTriggerPointerDown: React.PointerEventHandler<HTMLElement>;
  handleTriggerClick: React.MouseEventHandler<HTMLElement>;
  handleTriggerKeyDown: React.KeyboardEventHandler<HTMLElement>;
  handleTriggerFocus: React.FocusEventHandler<HTMLElement>;
  handleTriggerBlur: React.FocusEventHandler<HTMLElement>;
  handleContentPointerEnter: React.PointerEventHandler<HTMLElement>;
  handleContentPointerLeave: React.PointerEventHandler<HTMLElement>;
};

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

const DEFAULT_DELAY_DURATION_MS = 0;
const DEFAULT_SKIP_DELAY_DURATION_MS = 300;
/**
 * Matches the original project spacing between trigger and tooltip content.
 *
 * The invisible hover bridge below covers exactly this visual gap so users can
 * move the pointer from the trigger into the tooltip without flicker.
 */
const DEFAULT_SIDE_OFFSET_PX = 4;
/**
 * Extra cross-axis size for the invisible hover bridge.
 *
 * This makes diagonal movement into the tooltip forgiving without creating a
 * large sticky hover area.
 */
const TOOLTIP_HOVER_BRIDGE_CROSS_AXIS_PADDING_PX = 12;
const DEFAULT_COLLISION_PADDING_PX = 8;

/**
 * Base visual styling for floating tooltip content.
 *
 * Do not add `overflow-hidden` here: the invisible hover bridge extends beyond
 * the content box and would be clipped, reintroducing hover-gap flicker.
 */
const tooltipContentBaseClassName =
  "pointer-events-auto relative z-50 max-w-80 rounded-xl border border-border bg-popover px-3 py-2 text-base leading-snug text-popover-foreground shadow-lg outline-none";

/**
 * Tailwind-only invisible hover bridge attached to TooltipContent via ::before.
 *
 * The bridge covers the narrow visual `sideOffset` gap between trigger and
 * content. It is intentionally only as wide as that gap, with a small cross-axis
 * inset for natural diagonal entry, so it does not create sticky hover zones.
 * The JS diagonal corridor below solves a separate problem: realistic movement
 * from tiny triggers that leaves the trigger before reaching this direct gap.
 */
const tooltipHoverBridgeClassName =
  "before:absolute before:pointer-events-auto before:content-[''] data-[side=top]:before:top-full data-[side=top]:before:left-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=top]:before:right-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=top]:before:h-[var(--tooltip-hover-bridge-main-axis-size)] data-[side=bottom]:before:bottom-full data-[side=bottom]:before:left-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=bottom]:before:right-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=bottom]:before:h-[var(--tooltip-hover-bridge-main-axis-size)] data-[side=left]:before:left-full data-[side=left]:before:top-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=left]:before:bottom-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=left]:before:w-[var(--tooltip-hover-bridge-main-axis-size)] data-[side=right]:before:right-full data-[side=right]:before:top-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=right]:before:bottom-[var(--tooltip-hover-bridge-cross-axis-inset)] data-[side=right]:before:w-[var(--tooltip-hover-bridge-main-axis-size)]";

const tooltipAnimationClassName =
  "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 motion-reduce:animate-none motion-reduce:transition-none";

type Point = {
  x: number;
  y: number;
};

/**
 * Returns the current Tooltip context or fails fast when the tooltip parts are
 * used outside of Tooltip.
 */
function useTooltipContext(componentName: string): TooltipContextValue {
  const context = React.useContext(TooltipContext);

  if (!context) {
    throw new Error(`${componentName} must be used inside <Tooltip>.`);
  }

  return context;
}

/**
 * Runs the consumer event handler first, then runs the internal handler unless
 * the consumer prevented the default behavior.
 */
function composeEventHandlers<Event extends React.SyntheticEvent>(
  consumerHandler: ((event: Event) => void) | undefined,
  internalHandler: (event: Event) => void,
): (event: Event) => void {
  return (event) => {
    consumerHandler?.(event);

    if (!event.defaultPrevented) {
      internalHandler(event);
    }
  };
}

/**
 * Merges multiple refs into one callback ref.
 */
function composeRefs<Element>(
  ...refs: Array<React.Ref<Element | null> | undefined>
): React.RefCallback<Element> {
  return (node) => {
    for (const ref of refs) {
      if (!ref) continue;

      if (typeof ref === "function") {
        ref(node);
      } else {
        (ref as React.MutableRefObject<Element | null>).current = node;
      }
    }
  };
}

/**
 * Checks whether a pointer event represents true hover rather than contact.
 */
function isHoverPointer(event: React.PointerEvent | PointerEvent): boolean {
  return (
    (event.pointerType === "mouse" || event.pointerType === "pen") &&
    event.buttons === 0
  );
}

/**
 * Checks whether a viewport point is inside a DOMRect, optionally expanded by
 * a small padding value.
 */
function isPointInRect(
  x: number,
  y: number,
  rect: DOMRect,
  padding = 0,
): boolean {
  return (
    x >= rect.left - padding &&
    x <= rect.right + padding &&
    y >= rect.top - padding &&
    y <= rect.bottom + padding
  );
}

/**
 * Checks whether a viewport point is inside a polygon.
 */
function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;

  for (
    let currentIndex = 0, previousIndex = polygon.length - 1;
    currentIndex < polygon.length;
    previousIndex = currentIndex, currentIndex += 1
  ) {
    const current = polygon[currentIndex];
    const previous = polygon[previousIndex];

    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x <
        ((previous.x - current.x) * (point.y - current.y)) /
          (previous.y - current.y) +
          current.x;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Creates a narrow geometric corridor between trigger and tooltip content.
 *
 * The CSS pseudo-element bridge covers the direct `sideOffset` gap. This JS
 * corridor handles realistic diagonal movement that leaves tiny triggers before
 * the pointer reaches that direct gap, such as moving at roughly 45 degrees
 * from a small round icon to the tooltip.
 */
function createTooltipHoverCorridor(
  triggerRect: DOMRect,
  contentRect: DOMRect,
  padding: number,
): Point[] | null {
  if (contentRect.bottom <= triggerRect.top) {
    return [
      { x: contentRect.left - padding, y: contentRect.bottom - padding },
      { x: contentRect.right + padding, y: contentRect.bottom - padding },
      { x: triggerRect.right + padding, y: triggerRect.top + padding },
      { x: triggerRect.left - padding, y: triggerRect.top + padding },
    ];
  }

  if (contentRect.top >= triggerRect.bottom) {
    return [
      { x: triggerRect.left - padding, y: triggerRect.bottom - padding },
      { x: triggerRect.right + padding, y: triggerRect.bottom - padding },
      { x: contentRect.right + padding, y: contentRect.top + padding },
      { x: contentRect.left - padding, y: contentRect.top + padding },
    ];
  }

  if (contentRect.right <= triggerRect.left) {
    return [
      { x: contentRect.right - padding, y: contentRect.top - padding },
      { x: triggerRect.left + padding, y: triggerRect.top - padding },
      { x: triggerRect.left + padding, y: triggerRect.bottom + padding },
      { x: contentRect.right - padding, y: contentRect.bottom + padding },
    ];
  }

  if (contentRect.left >= triggerRect.right) {
    return [
      { x: triggerRect.right - padding, y: triggerRect.top - padding },
      { x: contentRect.left + padding, y: contentRect.top - padding },
      { x: contentRect.left + padding, y: contentRect.bottom + padding },
      { x: triggerRect.right - padding, y: triggerRect.bottom + padding },
    ];
  }

  return null;
}

/**
 * Controlled/uncontrolled boolean state helper used for tooltip open state.
 */
function useControllableBoolean({
  value,
  defaultValue = false,
  onChange,
}: {
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
}) {
  const [uncontrolledValue, setUncontrolledValue] =
    React.useState(defaultValue);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : uncontrolledValue;
  const currentValueRef = React.useRef(currentValue);

  // Keep callbacks reading the latest controlled value without changing the
  // public controlled/uncontrolled behavior.
  // eslint-disable-next-line react-hooks/refs
  currentValueRef.current = currentValue;

  const setValue = React.useCallback(
    (next: React.SetStateAction<boolean>) => {
      const previousValue = currentValueRef.current;
      const nextValue =
        typeof next === "function"
          ? (next as (previous: boolean) => boolean)(previousValue)
          : next;

      if (nextValue === previousValue) return;

      currentValueRef.current = nextValue;

      if (!isControlled) {
        setUncontrolledValue(nextValue);
      }

      onChange?.(nextValue);
    },
    [isControlled, onChange],
  );

  return [currentValue, setValue, currentValueRef] as const;
}

/**
 * Tracks the last real input modality across the document.
 *
 * This prevents touch-emulated mouse events on hybrid devices from opening
 * hover tooltips. After touch input, hover is suppressed until genuine
 * hover-capable mouse or pen movement is observed. This is intentional and not
 * time-based.
 */
const inputModalityTracker = (() => {
  let listenerCount = 0;
  let lastPointerDownType: "mouse" | "touch" | "pen" | null = null;
  let lastPointerDownSequence = 0;
  let lastHoverCapableActivitySequence = 0;
  let eventSequence = 0;
  let lastMouseClientX: number | null = null;
  let lastMouseClientY: number | null = null;

  function nextSequence(): number {
    eventSequence += 1;
    return eventSequence;
  }

  function handlePointerDown(event: PointerEvent): void {
    if (
      event.pointerType !== "mouse" &&
      event.pointerType !== "touch" &&
      event.pointerType !== "pen"
    ) {
      return;
    }

    lastPointerDownType = event.pointerType;
    lastPointerDownSequence = nextSequence();
    lastMouseClientX = null;
    lastMouseClientY = null;
  }

  function handlePointerMove(event: PointerEvent): void {
    if (event.pointerType === "pen") {
      if (event.buttons === 0) {
        lastHoverCapableActivitySequence = nextSequence();
      }

      return;
    }

    if (event.pointerType !== "mouse" || event.buttons !== 0) {
      return;
    }

    if (lastMouseClientX !== null && lastMouseClientY !== null) {
      const moved =
        event.clientX !== lastMouseClientX ||
        event.clientY !== lastMouseClientY;

      if (moved) {
        lastHoverCapableActivitySequence = nextSequence();
      }
    }

    lastMouseClientX = event.clientX;
    lastMouseClientY = event.clientY;
  }

  function handleMouseMove(event: MouseEvent): void {
    if (lastMouseClientX !== null && lastMouseClientY !== null) {
      const moved =
        event.clientX !== lastMouseClientX ||
        event.clientY !== lastMouseClientY;

      if (moved) {
        lastHoverCapableActivitySequence = nextSequence();
      }
    }

    lastMouseClientX = event.clientX;
    lastMouseClientY = event.clientY;
  }

  function handleMouseDown(): void {
    lastPointerDownType = "mouse";
    lastPointerDownSequence = nextSequence();
    lastMouseClientX = null;
    lastMouseClientY = null;
  }

  function handleTouchStart(): void {
    lastPointerDownType = "touch";
    lastPointerDownSequence = nextSequence();
    lastMouseClientX = null;
    lastMouseClientY = null;
  }

  function attach(): void {
    if (typeof document === "undefined") return;

    if (typeof window !== "undefined" && "PointerEvent" in window) {
      document.addEventListener("pointerdown", handlePointerDown, {
        capture: true,
        passive: true,
      });
      document.addEventListener("pointermove", handlePointerMove, {
        capture: true,
        passive: true,
      });
      return;
    }

    document.addEventListener("mousedown", handleMouseDown, {
      capture: true,
      passive: true,
    });
    document.addEventListener("mousemove", handleMouseMove, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchstart", handleTouchStart, {
      capture: true,
      passive: true,
    });
  }

  function detach(): void {
    if (typeof document === "undefined") return;

    if (typeof window !== "undefined" && "PointerEvent" in window) {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointermove", handlePointerMove, true);
      return;
    }

    document.removeEventListener("mousedown", handleMouseDown, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
    document.removeEventListener("touchstart", handleTouchStart, true);
  }

  return {
    /**
     * Registers the global listeners while at least one tooltip provider or
     * tooltip instance is mounted.
     */
    subscribe(): () => void {
      listenerCount += 1;

      if (listenerCount === 1) {
        attach();
      }

      return () => {
        listenerCount -= 1;

        if (listenerCount === 0) {
          detach();
        }
      };
    },

    /**
     * Returns true after touch input until genuine hover-capable pointer
     * movement has been observed.
     */
    shouldSuppressHover(): boolean {
      if (lastPointerDownType !== "touch") return false;

      return lastHoverCapableActivitySequence <= lastPointerDownSequence;
    },
  };
})();

/**
 * Applies the global input modality tracker for the lifetime of the component.
 */
function useInputModalityTracking(): void {
  React.useEffect(() => inputModalityTracker.subscribe(), []);
}

/**
 * Provides shared Radix tooltip timing configuration.
 *
 * Prefer rendering this once near the app root. Tooltip also subscribes to the
 * modality tracker itself, so individual tooltips remain safe during tests or
 * isolated rendering.
 */
function TooltipProvider({
  delayDuration = DEFAULT_DELAY_DURATION_MS,
  skipDelayDuration = DEFAULT_SKIP_DELAY_DURATION_MS,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) {
  useInputModalityTracking();

  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

type TooltipContentPrimitiveProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>;

type TooltipRootPrimitiveProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
>;

type TooltipBaseProps = Omit<
  TooltipRootPrimitiveProps,
  "children" | "open" | "defaultOpen" | "onOpenChange"
> &
  Pick<
    TooltipContentPrimitiveProps,
    | "align"
    | "alignOffset"
    | "avoidCollisions"
    | "collisionPadding"
    | "side"
    | "updatePositionStrategy"
  > & {
    interaction?: TooltipInteraction;
    disabled?: boolean;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    sideOffset?: TooltipContentPrimitiveProps["sideOffset"];
    showArrow?: boolean;
    arrowClassName?: string;
    /**
     * An element to treat as part of the tooltip besides the trigger, for a
     * trigger that only marks the position of a real control elsewhere in
     * the document (HoverTooltipDelegate).
     */
    interactiveElementRef?: React.RefObject<HTMLElement | null>;
  };

type TooltipContentValue = Exclude<React.ReactNode, undefined>;

/**
 * Shortcut Tooltip API.
 *
 * `children` must be one concrete trigger element because Tooltip passes it to
 * TooltipTrigger with `asChild` and renders TooltipContent automatically.
 */
type TooltipShortcutProps = TooltipBaseProps & {
  content: TooltipContentValue;
  children: React.ReactElement;
  contentClassName?: string;
};

/**
 * Composed Tooltip API.
 *
 * Omit `content` and render TooltipTrigger/TooltipContent manually as children.
 */
type TooltipComposedProps = TooltipBaseProps & {
  content?: undefined;
  children: React.ReactNode;
  contentClassName?: never;
};

type TooltipProps = TooltipShortcutProps | TooltipComposedProps;

/**
 * Shared tooltip component used by all tooltip variants.
 *
 * It centralizes the custom interaction model:
 * - `interaction="hover"` for action controls that open on mouse/pen hover and
 *   keyboard focus-visible without opening on touch or intercepting click,
 *   Enter or Space activation.
 * - `interaction="toggle"` for passive metadata controls that open on mouse/pen
 *   hover and keyboard focus-visible, toggle on click/tap/Enter/Space, and
 *   close on Escape or outside click. If keyboard focus already opened the
 *   tooltip, Enter or Space closes it because toggling follows the current open
 *   state; pressing again opens it.
 * - a shared hover region made from the trigger, content, CSS bridge and JS
 *   diagonal corridor. Leaving that region closes instantly; do not introduce
 *   timers or close delays.
 * - time-independent hover suppression after touch input on hybrid devices
 *   until real hover-capable mouse or pen movement is observed.
 */
function Tooltip({
  children,
  content,
  contentClassName,
  interaction = "hover",
  disabled = false,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  delayDuration = DEFAULT_DELAY_DURATION_MS,
  side = "top",
  align = "center",
  alignOffset,
  avoidCollisions,
  sideOffset = DEFAULT_SIDE_OFFSET_PX,
  collisionPadding = DEFAULT_COLLISION_PADDING_PX,
  updatePositionStrategy,
  showArrow = false,
  arrowClassName,
  interactiveElementRef,
  ...rootProps
}: TooltipProps) {
  useInputModalityTracking();

  const [open, setOpen, openRef] = useControllableBoolean({
    value: controlledOpen,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });

  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLElement | null>(null);
  const suppressHoverUntilPointerLeaveRef = React.useRef(false);
  const lastPointerTypeRef = React.useRef<string | null>(null);

  const isInsideTooltip = React.useCallback(
    (target: EventTarget | null) => {
      if (!(target instanceof Node)) return false;

      return (
        triggerRef.current?.contains(target) === true ||
        interactiveElementRef?.current?.contains(target) === true ||
        contentRef.current?.contains(target) === true
      );
    },
    [interactiveElementRef],
  );

  const shouldKeepOpenForPointer = React.useCallback((x: number, y: number) => {
    const trigger = triggerRef.current;
    const content = contentRef.current;

    const triggerRect = trigger?.getBoundingClientRect();
    const contentRect = content?.getBoundingClientRect();

    if (triggerRect && isPointInRect(x, y, triggerRect)) {
      return true;
    }

    if (contentRect && isPointInRect(x, y, contentRect)) {
      return true;
    }

    if (!triggerRect || !contentRect) {
      return false;
    }

    const hoverCorridor = createTooltipHoverCorridor(
      triggerRect,
      contentRect,
      TOOLTIP_HOVER_BRIDGE_CROSS_AXIS_PADDING_PX,
    );

    return hoverCorridor !== null && isPointInPolygon({ x, y }, hoverCorridor);
  }, []);

  const closeTooltip = React.useCallback(
    ({ suppressHoverUntilPointerLeave = false }: CloseTooltipOptions = {}) => {
      if (suppressHoverUntilPointerLeave) {
        suppressHoverUntilPointerLeaveRef.current = true;
      }

      setOpen(false);
    },
    [setOpen],
  );

  const openTooltip = React.useCallback(() => {
    if (disabled) return;

    suppressHoverUntilPointerLeaveRef.current = false;
    setOpen(true);
  }, [disabled, setOpen]);

  const closeIfPointerShouldNotKeepOpen = React.useCallback(
    (x: number, y: number) => {
      if (!shouldKeepOpenForPointer(x, y)) {
        setOpen(false);
      }
    },
    [setOpen, shouldKeepOpenForPointer],
  );

  const handleTriggerPointerEnter = React.useCallback<
    React.PointerEventHandler<HTMLElement>
  >(
    (event) => {
      lastPointerTypeRef.current = event.pointerType;

      if (disabled) return;
      if (!isHoverPointer(event)) return;
      if (inputModalityTracker.shouldSuppressHover()) return;
      if (suppressHoverUntilPointerLeaveRef.current) return;

      openTooltip();
    },
    [disabled, openTooltip],
  );

  const handleTriggerPointerLeave = React.useCallback<
    React.PointerEventHandler<HTMLElement>
  >(
    (event) => {
      lastPointerTypeRef.current = event.pointerType;

      if (event.pointerType === "touch") return;

      suppressHoverUntilPointerLeaveRef.current = false;

      if (isInsideTooltip(event.relatedTarget)) return;

      closeIfPointerShouldNotKeepOpen(event.clientX, event.clientY);
    },
    [closeIfPointerShouldNotKeepOpen, isInsideTooltip],
  );

  const handleTriggerPointerDown = React.useCallback<
    React.PointerEventHandler<HTMLElement>
  >(
    (event) => {
      lastPointerTypeRef.current = event.pointerType;

      if (event.pointerType === "touch") {
        suppressHoverUntilPointerLeaveRef.current = false;

        if (interaction === "hover") {
          setOpen(false);
        }
      }
    },
    [interaction, setOpen],
  );

  const handleTriggerClick = React.useCallback<
    React.MouseEventHandler<HTMLElement>
  >(
    (event) => {
      if (interaction !== "toggle") return;

      event.preventDefault();

      if (disabled) return;

      if (openRef.current) {
        closeTooltip({ suppressHoverUntilPointerLeave: true });
      } else {
        openTooltip();
      }
    },
    [closeTooltip, disabled, interaction, openRef, openTooltip],
  );

  const handleTriggerKeyDown = React.useCallback<
    React.KeyboardEventHandler<HTMLElement>
  >(
    (event) => {
      if (event.key === "Escape" && openRef.current) {
        event.preventDefault();
        event.stopPropagation();
        closeTooltip({ suppressHoverUntilPointerLeave: true });
        return;
      }

      if (
        interaction === "toggle" &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();

        if (openRef.current) {
          closeTooltip({ suppressHoverUntilPointerLeave: true });
        } else {
          openTooltip();
        }
      }
    },
    [closeTooltip, interaction, openRef, openTooltip],
  );

  const handleTriggerFocus = React.useCallback<
    React.FocusEventHandler<HTMLElement>
  >(
    (event) => {
      if (disabled) return;
      if (lastPointerTypeRef.current === "touch") return;

      if (
        typeof event.currentTarget.matches === "function" &&
        !event.currentTarget.matches(":focus-visible")
      ) {
        return;
      }

      openTooltip();
    },
    [disabled, openTooltip],
  );

  const handleTriggerBlur = React.useCallback<
    React.FocusEventHandler<HTMLElement>
  >(
    (event) => {
      if (isInsideTooltip(event.relatedTarget)) return;
      if (lastPointerTypeRef.current === "touch") return;

      setOpen(false);
    },
    [isInsideTooltip, setOpen],
  );

  const handleContentPointerEnter = React.useCallback<
    React.PointerEventHandler<HTMLElement>
  >(
    (event) => {
      lastPointerTypeRef.current = event.pointerType;

      if (disabled) return;
      if (!isHoverPointer(event)) return;
      if (!openRef.current) return;
      if (inputModalityTracker.shouldSuppressHover()) return;
      if (suppressHoverUntilPointerLeaveRef.current) return;

      openTooltip();
    },
    [disabled, openRef, openTooltip],
  );

  const handleContentPointerLeave = React.useCallback<
    React.PointerEventHandler<HTMLElement>
  >(
    (event) => {
      lastPointerTypeRef.current = event.pointerType;

      if (event.pointerType === "touch") return;

      suppressHoverUntilPointerLeaveRef.current = false;

      if (isInsideTooltip(event.relatedTarget)) return;

      closeIfPointerShouldNotKeepOpen(event.clientX, event.clientY);
    },
    [closeIfPointerShouldNotKeepOpen, isInsideTooltip],
  );

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return;

    const handlePointerMove = (event: PointerEvent) => {
      if (!isHoverPointer(event)) return;

      const shouldKeepOpen = shouldKeepOpenForPointer(
        event.clientX,
        event.clientY,
      );

      if (suppressHoverUntilPointerLeaveRef.current) {
        if (!shouldKeepOpen) {
          suppressHoverUntilPointerLeaveRef.current = false;
        }

        return;
      }

      if (!shouldKeepOpen) {
        setOpen(false);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const shouldKeepOpen = shouldKeepOpenForPointer(
        event.clientX,
        event.clientY,
      );

      if (suppressHoverUntilPointerLeaveRef.current) {
        if (!shouldKeepOpen) {
          suppressHoverUntilPointerLeaveRef.current = false;
        }

        return;
      }

      if (!shouldKeepOpen) {
        setOpen(false);
      }
    };

    if (typeof window !== "undefined" && "PointerEvent" in window) {
      document.addEventListener("pointermove", handlePointerMove, {
        capture: true,
        passive: true,
      });

      return () => {
        document.removeEventListener("pointermove", handlePointerMove, true);
      };
    }

    document.addEventListener("mousemove", handleMouseMove, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove, true);
    };
  }, [open, setOpen, shouldKeepOpenForPointer]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return;

    const handlePointerDown = (event: PointerEvent) => {
      lastPointerTypeRef.current = event.pointerType;

      if (!isInsideTooltip(event.target)) {
        closeTooltip();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      lastPointerTypeRef.current = "mouse";

      if (!isInsideTooltip(event.target)) {
        closeTooltip();
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      lastPointerTypeRef.current = "touch";

      if (!isInsideTooltip(event.target)) {
        closeTooltip();
      }
    };

    if (typeof window !== "undefined" && "PointerEvent" in window) {
      document.addEventListener("pointerdown", handlePointerDown, true);

      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
      };
    }

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("touchstart", handleTouchStart, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("touchstart", handleTouchStart, true);
    };
  }, [closeTooltip, isInsideTooltip, open]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return;

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      event.stopPropagation();
      closeTooltip({ suppressHoverUntilPointerLeave: true });
    };

    document.addEventListener("keydown", handleDocumentKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown, true);
    };
  }, [closeTooltip, open]);

  React.useEffect(() => {
    if (disabled && openRef.current) {
      closeTooltip();
    }
  }, [closeTooltip, disabled, openRef]);

  const contextValue = React.useMemo<TooltipContextValue>(
    () => ({
      open,
      disabled,
      interaction,
      triggerRef,
      contentRef,
      openTooltip,
      closeTooltip,
      isInsideTooltip,
      handleTriggerPointerEnter,
      handleTriggerPointerLeave,
      handleTriggerPointerDown,
      handleTriggerClick,
      handleTriggerKeyDown,
      handleTriggerFocus,
      handleTriggerBlur,
      handleContentPointerEnter,
      handleContentPointerLeave,
    }),
    [
      closeTooltip,
      disabled,
      handleContentPointerEnter,
      handleContentPointerLeave,
      handleTriggerBlur,
      handleTriggerClick,
      handleTriggerFocus,
      handleTriggerKeyDown,
      handleTriggerPointerDown,
      handleTriggerPointerEnter,
      handleTriggerPointerLeave,
      interaction,
      isInsideTooltip,
      open,
      openTooltip,
    ],
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        delayDuration={delayDuration}
        open={open}
        {...rootProps}
      >
        {content === undefined ? (
          children
        ) : (
          <>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent
              align={align}
              alignOffset={alignOffset}
              avoidCollisions={avoidCollisions}
              className={contentClassName}
              collisionPadding={collisionPadding}
              updatePositionStrategy={updatePositionStrategy}
              side={side}
              sideOffset={sideOffset}
              showArrow={showArrow}
              arrowClassName={arrowClassName}
            >
              {content}
            </TooltipContent>
          </>
        )}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  );
}

/**
 * Trigger element for Tooltip.
 *
 * Use `asChild` for real controls such as buttons and links so the trigger
 * keeps the correct semantic element.
 *
 * Toggle triggers expose `aria-expanded` so assistive technologies can
 * understand the disclosure-like state used by Hint, LanguageFlag,
 * JavaChampionBadge and small metadata badges. Keep the trigger itself a
 * semantic button or real interactive element when using `asChild`.
 */
const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(
  (
    {
      onPointerEnter,
      onPointerLeave,
      onPointerDown,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      "aria-expanded": ariaExpanded,
      ...props
    },
    forwardedRef,
  ) => {
    const context = useTooltipContext("TooltipTrigger");

    return (
      <TooltipPrimitive.Trigger
        ref={composeRefs(
          forwardedRef as React.Ref<HTMLElement>,
          context.triggerRef,
        )}
        data-slot="tooltip-trigger"
        aria-expanded={
          ariaExpanded ??
          (context.interaction === "toggle" ? context.open : undefined)
        }
        {...props}
        onPointerEnter={composeEventHandlers(
          onPointerEnter,
          context.handleTriggerPointerEnter,
        )}
        onPointerLeave={composeEventHandlers(
          onPointerLeave,
          context.handleTriggerPointerLeave,
        )}
        onPointerDown={composeEventHandlers(
          onPointerDown,
          context.handleTriggerPointerDown,
        )}
        onClick={composeEventHandlers(onClick, context.handleTriggerClick)}
        onKeyDown={composeEventHandlers(
          onKeyDown,
          context.handleTriggerKeyDown,
        )}
        onFocus={composeEventHandlers(onFocus, context.handleTriggerFocus)}
        onBlur={composeEventHandlers(onBlur, context.handleTriggerBlur)}
      />
    );
  },
);
TooltipTrigger.displayName = "TooltipTrigger";

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> & {
  showArrow?: boolean;
  arrowClassName?: string;
};

/**
 * Floating content rendered in a portal.
 *
 * The content participates in the shared hover region, so the tooltip stays
 * open while the pointer is over the trigger, content, CSS bridge or JS
 * diagonal corridor. Leaving that shared region closes instantly.
 */
const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      children,
      sideOffset = DEFAULT_SIDE_OFFSET_PX,
      collisionPadding = DEFAULT_COLLISION_PADDING_PX,
      showArrow = false,
      arrowClassName,
      onPointerEnter,
      onPointerLeave,
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const context = useTooltipContext("TooltipContent");
    const hoverBridgeMainAxisSize =
      typeof sideOffset === "number"
        ? Math.max(sideOffset, 0)
        : DEFAULT_SIDE_OFFSET_PX;
    const contentStyle = {
      "--tooltip-hover-bridge-main-axis-size": `${hoverBridgeMainAxisSize}px`,
      "--tooltip-hover-bridge-cross-axis-inset": `-${TOOLTIP_HOVER_BRIDGE_CROSS_AXIS_PADDING_PX}px`,
      ...style,
    } as React.CSSProperties;

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={composeRefs(
            forwardedRef as React.Ref<HTMLElement>,
            context.contentRef,
          )}
          data-slot="tooltip-content"
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          style={contentStyle}
          className={cn(
            tooltipContentBaseClassName,
            tooltipHoverBridgeClassName,
            tooltipAnimationClassName,
            "origin-(--radix-tooltip-content-transform-origin)",
            className,
          )}
          {...props}
          onPointerEnter={composeEventHandlers(
            onPointerEnter,
            context.handleContentPointerEnter,
          )}
          onPointerLeave={composeEventHandlers(
            onPointerLeave,
            context.handleContentPointerLeave,
          )}
        >
          {children}

          {showArrow ? (
            <TooltipPrimitive.Arrow
              data-slot="tooltip-arrow"
              className={cn("fill-popover", arrowClassName)}
            />
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = "TooltipContent";

/**
 * Optional arrow for composed tooltip content.
 */
const TooltipArrow = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    data-slot="tooltip-arrow"
    className={cn("fill-popover", className)}
    {...props}
  />
));
TooltipArrow.displayName = "TooltipArrow";

const TOOLTIP_TARGET_ATTRIBUTE = "data-tooltip";
const TOOLTIP_SIDE_ATTRIBUTE = "data-tooltip-side";
const TOOLTIP_TARGET_SELECTOR = `[${TOOLTIP_TARGET_ATTRIBUTE}]`;
const TOOLTIP_SIDES = [
  "top",
  "right",
  "bottom",
  "left",
] as const satisfies readonly NonNullable<TooltipProps["side"]>[];

type DelegateTarget = {
  element: HTMLElement;
  content: string;
  side: TooltipProps["side"];
  anchorStyle: React.CSSProperties;
};

function sideOf(element: HTMLElement): TooltipProps["side"] {
  const side = element.getAttribute(TOOLTIP_SIDE_ATTRIBUTE);
  return TOOLTIP_SIDES.find((candidate) => candidate === side) ?? "top";
}

function findTooltipTarget(node: EventTarget | null) {
  return node instanceof Element
    ? node.closest<HTMLElement>(TOOLTIP_TARGET_SELECTOR)
    : null;
}

// True when a pointerover or pointerout crosses the element's boundary rather
// than moving between its descendants.
function crossesBoundary(
  element: HTMLElement,
  relatedTarget: EventTarget | null,
) {
  return !(relatedTarget instanceof Node && element.contains(relatedTarget));
}

/**
 * One hover tooltip for every element on the page that carries
 * `data-tooltip`. Rendered once, in the root layout.
 *
 * A Tooltip per trigger costs a Radix root, a popper and two post-mount
 * state updates each; the events table alone had 243 of them, and every page
 * paid for the header's and the scroll-to-top button's. This listens on the
 * document and lays a single tooltip's invisible anchor over whichever
 * trigger the pointer or keyboard focus reaches. It opens for mouse and pen
 * hover and keyboard focus-visible, never for touch, and Escape keeps it
 * closed until the pointer leaves that trigger. The trigger's `data-tooltip`
 * is read again whenever it changes while the tooltip is open, so a control
 * whose label flips on click (the theme toggle) stays right, and the trigger
 * carries aria-describedby for as long as its tooltip is open. A trigger
 * picks its side with `data-tooltip-side`.
 *
 * The anchor is positioned absolutely, so this must render inside a
 * positioned ancestor. Deleting this in favour of a Tooltip per trigger
 * brings the per-instance cost back; see
 * adr/0012-share-one-hover-tooltip-across-the-page.md.
 */
function HoverTooltipDelegate() {
  useInputModalityTracking();

  const anchorRef = React.useRef<HTMLSpanElement | null>(null);
  const activeElementRef = React.useRef<HTMLElement | null>(null);
  const suppressedElementRef = React.useRef<HTMLElement | null>(null);
  const [target, setTarget] = React.useState<DelegateTarget | null>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const show = (element: HTMLElement) => {
      const container = anchorRef.current?.offsetParent;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      setTarget({
        element,
        content: element.getAttribute(TOOLTIP_TARGET_ATTRIBUTE) ?? "",
        side: sideOf(element),
        anchorStyle: {
          left: rect.left - containerRect.left + container.scrollLeft,
          top: rect.top - containerRect.top + container.scrollTop,
          width: rect.width,
          height: rect.height,
        },
      });
      setOpen(true);
    };

    const onPointerOver = (event: PointerEvent) => {
      const element = findTooltipTarget(event.target);
      if (!element || !crossesBoundary(element, event.relatedTarget)) return;
      if (!isHoverPointer(event)) return;
      if (inputModalityTracker.shouldSuppressHover()) return;
      if (suppressedElementRef.current === element) return;
      show(element);
    };
    const onPointerOut = (event: PointerEvent) => {
      const element = findTooltipTarget(event.target);
      if (!element || !crossesBoundary(element, event.relatedTarget)) return;
      if (suppressedElementRef.current === element) {
        suppressedElementRef.current = null;
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch" && findTooltipTarget(event.target)) {
        setOpen(false);
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      const element = findTooltipTarget(event.target);
      if (!element || !element.matches(":focus-visible")) return;
      show(element);
    };
    const onFocusOut = (event: FocusEvent) => {
      if (findTooltipTarget(event.target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const element = findTooltipTarget(event.target);
      if (!element) return;
      suppressedElementRef.current = element;
      setOpen(false);
    };

    const onScroll = (event: Event) => {
      // A tooltip rides along with its trigger through a scroll. The anchor
      // sits outside the scrolling container, so it is moved to where the
      // trigger now is; the content follows the anchor (see the Tooltip's
      // updatePositionStrategy below).
      const element = activeElementRef.current;
      if (
        element &&
        event.target instanceof Node &&
        event.target.contains(element)
      ) {
        show(element);
      }
    };
    const listeners: {
      [K in keyof DocumentEventMap]?: (event: DocumentEventMap[K]) => void;
    } = {
      pointerover: onPointerOver,
      pointerout: onPointerOut,
      pointerdown: onPointerDown,
      focusin: onFocusIn,
      focusout: onFocusOut,
      keydown: onKeyDown,
      scroll: onScroll,
    };
    for (const [type, listener] of Object.entries(listeners)) {
      document.addEventListener(type, listener as EventListener, true);
    }
    return () => {
      for (const [type, listener] of Object.entries(listeners)) {
        document.removeEventListener(type, listener as EventListener, true);
      }
    };
  }, []);

  // A trigger whose label changes while its tooltip is open (the theme
  // toggle after a click) updates the tooltip text in place.
  React.useEffect(() => {
    if (!open || !target) return;
    const observer = new MutationObserver(() => {
      const content = target.element.getAttribute(TOOLTIP_TARGET_ATTRIBUTE);
      if (content === null) {
        setOpen(false);
        return;
      }
      setTarget((current) =>
        current && current.element === target.element
          ? { ...current, content }
          : current,
      );
    });
    observer.observe(target.element, {
      attributes: true,
      attributeFilter: [TOOLTIP_TARGET_ATTRIBUTE],
    });
    return () => observer.disconnect();
  }, [open, target]);

  // While open, the real trigger counts as part of the tooltip (so a click on
  // it does not close it as an outside click) and keeps the aria-describedby
  // link a Tooltip trigger would carry.
  React.useEffect(() => {
    const element = open ? (target?.element ?? null) : null;
    activeElementRef.current = element;
    const anchor = anchorRef.current;
    if (!element || !anchor) return;
    const describedBy = anchor.getAttribute("aria-describedby");
    if (!describedBy) return;
    element.setAttribute("aria-describedby", describedBy);
    return () => element.removeAttribute("aria-describedby");
  }, [open, target]);

  // A tooltip does not outlive its trigger: the go-back button unmounts on
  // activation, a hovered events row can be filtered away by a search.
  React.useEffect(() => {
    if (!open || !target) return;
    const observer = new MutationObserver(() => {
      if (!target.element.isConnected) setOpen(false);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, target]);

  return (
    <Tooltip
      content={target?.content ?? ""}
      open={open && target !== null}
      onOpenChange={setOpen}
      interaction="hover"
      side={target?.side ?? "top"}
      interactiveElementRef={activeElementRef}
      updatePositionStrategy="always"
    >
      <span
        ref={anchorRef}
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={target?.anchorStyle}
      />
    </Tooltip>
  );
}

export {
  HoverTooltipDelegate,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
};

export type { TooltipContentProps, TooltipInteraction, TooltipProps };
